import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { translations, detectInitialLang, saveLang } from '@/i18n';
import { setApiLang } from '@/lib/api';

const LanguageContext = createContext(null);

// Walk a dot-path (e.g. "dashboard.subGreeting") through a nested object.
const resolvePath = (obj, path) =>
  path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);

// Replace {placeholders} with values from `vars`.
const interpolate = (str, vars) => {
  if (!vars || typeof str !== 'string') return str;
  return str.replace(/\{(\w+)\}/g, (_, key) => (vars[key] != null ? String(vars[key]) : `{${key}}`));
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => detectInitialLang());

  // Keep the axios layer + <html lang> in sync so backend-localized responses
  // and accessibility tooling agree with the UI.
  useEffect(() => {
    setApiLang(lang);
    try {
      document.documentElement.lang = lang;
    } catch {
      // document may be unavailable during SSR-style execution; ignore.
    }
  }, [lang]);

  const setLang = useCallback((next) => {
    setLangState(next);
    saveLang(next);
  }, []);

  // Translate a dot-path key, falling back to English, then to the raw key.
  const t = useCallback(
    (key, vars) => {
      const localized = resolvePath(translations[lang], key);
      const fallback = localized != null ? localized : resolvePath(translations.en, key);
      return interpolate(fallback != null ? fallback : key, vars);
    },
    [lang]
  );

  // Translate an enum VALUE's display label only — the value itself is unchanged
  // by the caller, so storage/filter/chip-color logic keeps using English.
  const tEnum = useCallback(
    (value) => {
      if (value == null) return value;
      const map = translations[lang]?.enums || {};
      const fallbackMap = translations.en.enums || {};
      return map[value] != null ? map[value] : (fallbackMap[value] != null ? fallbackMap[value] : value);
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t, tEnum }), [lang, setLang, t, tEnum]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang must be used within a LanguageProvider');
  return ctx;
};
